/*
 * THESIS: an edition reads as one dispatch, not a feed of cards. Three highlights lead as wide screenshot spreads (one
 * lead, two seconds), then every other change sits in a ruled list keyed by a hanging product-area column. It refuses
 * the uniform grid of same-size change cards most changelogs ship.
 * OWN-WORLD: the help desk's V2 language unchanged: navy ink on white, hairline rules, 12px soft rectangles only around
 * screenshots, tinted pills for New (green tint, the one green voice), Improved (cyan) and Fixed (neutral).
 * STORY: a consultant opens Friday's notes, sees in seconds what is worth trying, scans their own areas, and follows a
 * guide link to try it.
 * FIRST VIEWPORT: back link, kicker and period title, publish line; the lead highlight's screenshot full width below.
 * FORM: hanging-column dispatch (sixth of seven structures); staging: a stepped "In this edition" area rail at xl
 * widths. Seed 340e563b.
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight, ImageOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useReleaseNoteImage } from '@/queries/releaseNotes';
import { areaName, areaOrder } from '@/lib/productAreas';
import { cn } from '@/lib/utils';
import type { ReaderEdition, ReaderHighlight, ReaderItem, ReleaseNoteType } from '@/types/releaseNotes';
import { changeCount, periodLabel, publishedLabel, TYPE_LABELS } from './format';

const TYPE_VARIANT: Record<ReleaseNoteType, 'default' | 'info' | 'neutral'> = { new: 'default', improved: 'info', fixed: 'neutral' };

export function TypeTag({ type }: { type: ReleaseNoteType }) {
  return (
    <Badge variant={TYPE_VARIANT[type]} data-testid="release-note-type">
      {TYPE_LABELS[type]}
    </Badge>
  );
}

function GuideLink({ slug, enabled }: { slug: string | null; enabled: boolean }) {
  if (!slug) return null;
  const className = 'inline-flex items-center gap-1 text-etn-control-sm font-semibold text-etn-accent-text-active hover:underline';
  // In the admin preview the guide may not be published yet, so it is named rather than linked.
  if (!enabled) {
    return <span className={cn(className, 'text-etn-text-label hover:no-underline')}>Guide: {slug}</span>;
  }
  return (
    <Link to="/kb/$slug" params={{ slug }} className={className}>
      Read the guide
      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </Link>
  );
}

function Screenshot({ highlight, className }: { highlight: ReaderHighlight; className?: string }) {
  const image = useReleaseNoteImage(highlight.imageS3Key);
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-etn-card border border-etn-border-hairline bg-etn-surface-sunken shadow-etn-whisper',
        className
      )}
    >
      {image.isLoading && <Skeleton className="absolute inset-0 rounded-none" />}
      {image.isError && (
        <div className="absolute inset-0 flex items-center justify-center text-etn-text-soft">
          <ImageOff className="h-6 w-6" aria-label="Screenshot unavailable" />
        </div>
      )}
      {image.data && (
        <img src={image.data} alt={highlight.imageAlt} className="absolute inset-0 h-full w-full object-cover object-top" loading="lazy" />
      )}
    </div>
  );
}

function Highlight({ highlight, item, lead, linkGuides }: { highlight: ReaderHighlight; item: ReaderItem; lead: boolean; linkGuides: boolean }) {
  return (
    <figure className={cn('min-w-0', lead && 'md:col-span-2')} data-testid="release-note-highlight">
      <Screenshot highlight={highlight} className={lead ? 'aspect-[16/8]' : 'aspect-[16/10]'} />
      <figcaption className={cn('mt-4 space-y-1.5', lead && 'max-w-[65ch]')}>
        <div className="flex items-center gap-2">
          <TypeTag type={item.type} />
          <span className="text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">{areaName(item.area)}</span>
        </div>
        <h3 className={cn('font-semibold text-etn-text', lead ? 'text-lg leading-snug' : 'text-base leading-snug')}>{item.title}</h3>
        <p className="text-sm leading-relaxed text-etn-text-cell">{item.body}</p>
        <GuideLink slug={item.guideSlug} enabled={linkGuides} />
      </figcaption>
    </figure>
  );
}

interface AreaGroup {
  area: string;
  items: ReaderItem[];
}

function groupByArea(items: ReaderItem[]): AreaGroup[] {
  const groups = items.reduce<Map<string, ReaderItem[]>>((acc, item) => acc.set(item.area, [...(acc.get(item.area) ?? []), item]), new Map());
  return [...groups.entries()].sort(([a], [b]) => areaOrder(a) - areaOrder(b)).map(([area, list]) => ({ area, items: list }));
}

function AreaGroups({ groups, idPrefix, linkGuides }: { groups: AreaGroup[]; idPrefix: string; linkGuides: boolean }) {
  return (
    <div>
      {groups.map(group => (
        <section
          key={group.area}
          id={`${idPrefix}-${group.area}`}
          aria-labelledby={`${idPrefix}-${group.area}-heading`}
          className="grid scroll-mt-6 gap-x-8 gap-y-2 border-t border-etn-border-hairline py-5 md:grid-cols-[10.5rem_1fr]"
        >
          <h3 id={`${idPrefix}-${group.area}-heading`} className="pt-0.5 text-etn-control font-semibold text-etn-text-label">
            {areaName(group.area)}
          </h3>
          <ul className="divide-y divide-etn-border-row">
            {group.items.map(item => (
              <li key={item._id} className="space-y-1 py-3 first:pt-0 last:pb-0" data-testid="release-note-item">
                <div className="flex items-start gap-2.5">
                  <TypeTag type={item.type} />
                  <h4 className="text-sm font-semibold leading-snug text-etn-text">{item.title}</h4>
                </div>
                <p className="max-w-[65ch] text-etn-cell leading-relaxed text-etn-text-cell">{item.body}</p>
                <GuideLink slug={item.guideSlug} enabled={linkGuides} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function AreaRail({ groups, partnerGroups }: { groups: AreaGroup[]; partnerGroups: AreaGroup[] }) {
  const entry = (prefix: string) => (group: AreaGroup) => (
    <li key={`${prefix}-${group.area}`}>
      <a
        href={`#${prefix}-${group.area}`}
        className="flex items-baseline justify-between gap-3 rounded-[6px] px-2 py-1 text-etn-cell text-etn-text-cell hover:bg-etn-surface-sunken hover:text-etn-text"
      >
        <span className="truncate">{areaName(group.area)}</span>
        <span className="text-etn-micro text-etn-text-soft tabular-nums">{group.items.length}</span>
      </a>
    </li>
  );
  return (
    <nav aria-label="In this edition" className="sticky top-6 hidden xl:block">
      <p className="mb-2 px-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">In this edition</p>
      <ul className="space-y-0.5 border-l border-etn-border-hairline pl-2">{groups.map(entry('area'))}</ul>
      {partnerGroups.length > 0 && (
        <>
          <p className="mb-2 mt-5 px-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">For partners</p>
          <ul className="space-y-0.5 border-l border-etn-border-hairline pl-2">{partnerGroups.map(entry('partner'))}</ul>
        </>
      )}
    </nav>
  );
}

/**
 * One edition as a reader sees it. Used by the reader page and by the review screen's preview, where guides are named
 * rather than linked (`linkGuides={false}`).
 */
export function EditionArticle({ edition, linkGuides = true }: { edition: ReaderEdition; linkGuides?: boolean }) {
  const itemsById = new Map(edition.items.map(item => [item._id, item]));
  const highlights = edition.highlights.flatMap(h => {
    const item = itemsById.get(h.itemId);
    return item ? [{ highlight: h, item }] : [];
  });
  const highlighted = new Set(highlights.map(h => h.item._id));
  const rest = edition.items.filter(item => !highlighted.has(item._id));
  const groups = groupByArea(rest.filter(item => item.audience === 'everyone'));
  const partnerGroups = groupByArea(rest.filter(item => item.audience === 'partner'));

  return (
    <article className="space-y-10" data-testid="release-note-edition">
      <header className="space-y-1.5">
        <p className="text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">
          {edition.kind === 'backfill' ? 'Monthly round-up' : 'Release notes'}
        </p>
        <h1 className="text-2xl font-semibold text-etn-text">{periodLabel(edition)}</h1>
        <p className="text-etn-cell text-etn-text-label">
          Published {publishedLabel(edition.publishAt)} · {changeCount(edition.items.length)}
        </p>
      </header>

      {highlights.length > 0 && (
        <section aria-label="Highlights" className="grid gap-x-8 gap-y-10 md:grid-cols-2">
          {highlights.map(({ highlight, item }, i) => (
            <Highlight key={item._id} highlight={highlight} item={item} lead={i === 0} linkGuides={linkGuides} />
          ))}
        </section>
      )}

      {(groups.length > 0 || partnerGroups.length > 0) && (
        <div className="grid gap-12 xl:grid-cols-[minmax(0,1fr)_12rem]">
          <div className="min-w-0 space-y-10">
            {groups.length > 0 && (
              <section aria-labelledby="release-notes-everything-else">
                <h2 id="release-notes-everything-else" className="mb-1 text-lg font-semibold text-etn-text">
                  {highlights.length ? 'Everything else' : 'What changed'}
                </h2>
                <AreaGroups groups={groups} idPrefix="area" linkGuides={linkGuides} />
              </section>
            )}
            {partnerGroups.length > 0 && (
              <section
                aria-labelledby="release-notes-partners"
                className="rounded-etn-card bg-etn-surface-sunken px-6 pb-2 pt-5"
                data-testid="release-notes-partners"
              >
                <h2 id="release-notes-partners" className="text-lg font-semibold text-etn-text">
                  For partners
                </h2>
                <p className="mb-3 mt-1 text-etn-cell text-etn-text-label">
                  Settings and tools for running your clients, shown to partner Company Admins.
                </p>
                <AreaGroups groups={partnerGroups} idPrefix="partner" linkGuides={linkGuides} />
              </section>
            )}
          </div>
          <AreaRail groups={groups} partnerGroups={partnerGroups} />
        </div>
      )}
    </article>
  );
}
